import { useState } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useUIStore } from '../../stores/uiStore';
import { useFileStore } from '../../stores/fileStore';
import { useAuthStore } from '../../stores/authStore';
import { filesApi } from '../../api/files';
import { foldersApi } from '../../api/folders';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toaster';
import { useLocation } from 'react-router-dom';

export function DeleteConfirmDialog() {
  const { t } = useTranslation('files');
  const [isLoading, setIsLoading] = useState(false);

  const isOpen = useUIStore((s) => s.isDeleteConfirmOpen);
  const closeModal = useUIStore((s) => s.closeDeleteConfirm);
  const contextMenuTarget = useUIStore((s) => s.contextMenuTarget);

  const files = useFileStore((s) => s.files);
  const folders = useFileStore((s) => s.folders);
  const selectedIds = useFileStore((s) => s.selectedIds);
  const removeFile = useFileStore((s) => s.removeFile);
  const removeFolder = useFileStore((s) => s.removeFolder);
  const clearSelection = useFileStore((s) => s.clearSelection);
  const updateStorage = useAuthStore((s) => s.updateStorage);
  const toast = useToast();
  const location = useLocation();

  const isTrashPage = location.pathname === '/trash';

  // Determine what to delete
  const itemsToDelete =
    selectedIds.length > 0
      ? selectedIds
      : contextMenuTarget
        ? [contextMenuTarget.id]
        : [];

  const fileCount = itemsToDelete.filter((id) =>
    files.some((f) => f.id === id)
  ).length;
  const folderCount = itemsToDelete.filter((id) =>
    folders.some((f) => f.id === id)
  ).length;

  const getItemDescription = () => {
    const parts = [];
    if (fileCount > 0) {
      parts.push(t('delete.fileCount', { count: fileCount }));
    }
    if (folderCount > 0) {
      parts.push(t('delete.folderCount', { count: folderCount }));
    }
    return parts.join(` ${t('common:and')} `);
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      let lastStorageUsed: number | null = null;

      for (const id of itemsToDelete) {
        const isFile = files.some((f) => f.id === id);
        const isFolder = folders.some((f) => f.id === id);

        if (isFile) {
          if (isTrashPage) {
            const result = await filesApi.deletePermanently(id);
            lastStorageUsed = result.storageUsed;
          } else {
            await filesApi.delete(id);
          }
          removeFile(id);
        } else if (isFolder) {
          const result = await foldersApi.delete(id);
          lastStorageUsed = result.storageUsed;
          removeFolder(id);
        }
      }

      // Update storage in UI if we got a new value
      if (lastStorageUsed !== null) {
        updateStorage(lastStorageUsed);
      }

      clearSelection();
      toast.success(
        isTrashPage ? t('toast.permanentlyDeleted') : t('toast.movedToTrash'),
        isTrashPage
          ? t('toast.permanentlyDeletedDesc', { items: getItemDescription() })
          : t('toast.movedToTrashDesc', { items: getItemDescription() })
      );
      handleClose();
    } catch {
      toast.error(t('toast.deleteFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    closeModal();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" size="sm">
      <div className="text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <ExclamationTriangleIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
        </motion.div>

        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {isTrashPage ? t('delete.permanentTitle') : t('delete.trashTitle')}
        </h3>

        <p className="text-gray-500 dark:text-slate-400 mb-6">
          {isTrashPage ? (
            <>
              {t('delete.permanentMessage', { items: getItemDescription() })}
              <br />
              <span className="text-red-500 dark:text-red-400 font-medium">
                {t('delete.cannotUndo')}
              </span>
            </>
          ) : (
            <>
              {t('delete.trashMessage', { items: getItemDescription() })}
              <br />
              {t('delete.canRestore')}
            </>
          )}
        </p>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            className="flex-1"
          >
            {t('common:buttons.cancel')}
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleDelete}
            isLoading={isLoading}
            className="flex-1"
          >
            {isTrashPage ? t('delete.deleteForever') : t('delete.moveToTrash')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
